// node_modules
import React, { useEffect, useState, useRef, useContext } from "react";
import { useDispatch } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import { useReactToPrint } from "react-to-print";
import { format, parse } from "date-fns";

// primereact components
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Sidebar } from "primereact/sidebar";
import { Accordion, AccordionTab } from "primereact/accordion";
import { Toast } from "primereact/toast";

// components
import OrderCard from "./OrderCard";
import { SendStationStatisticModal } from "./components/SendStationStatisticModal/SendStationStatisticModal";

// hooks
import { useWS } from "../../hooks/useWS";

// redux
import {
  foodStationApi,
  useGetStationListQuery,
} from "../../redux/services/foodStation";
import {
  useGetChefStatisticQuery,
  statisticApi,
} from "../../redux/services/statistic";
import {
  useGetStationOptionsListQuery,
  useUpdateOptionMutation,
  useUpdateOptionGroupMutation,
  foodStationOptionsApi,
} from "../../redux/services/foodStationOptions";
import { useMakeOrderCompletedMutation } from "../../redux/services/order";
import { setLoading } from "../../redux/slices/app.slice";

// utils
import getSubdomain from "../../utils/getSubdomain";
import { shouldAutoDisableGroup } from "../../utils/optionFilters";
import sortMenuByStationOptions from "../../utils/sortMenuByStationOptions";
import { convertForReader, convertToNormal } from "../../utils/reader4digits";

// context
import { ToastContext } from "../../App";

// assets
import eyeGearIcon from "../../assets/eye-gear.svg";

// styles
import * as styles from "./Station.module.css";
import { useSendStation } from "../../context/SendStationContext";
import { generateReportFile } from "./components/SendStationStatisticModal/services/generateReportFile";

const WarningComplete = ({
  id,
  orderNumber,
  toast,
  makeOrderCompleted,
  input,
}) => {
  const [timer, setTimer] = useState(6);

  const tick = useRef(null);

  useEffect(() => {
    console.log("WarningComplete mounted", id);
    tick.current = setInterval(() => {
      setTimer((prev) => {
        if (prev === 1) {
          makeOrderCompleted({ id });
          toast.clear();
          input.value = "";
          return prev;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      clearInterval(tick.current);
    };
  }, []);

  return (
    <div>
      <div style={{ marginBottom: "20px" }}>
        Order №{orderNumber} will be marked as completed after {timer - 1}{" "}
        seconds
      </div>
      <Button
        style={{ background: "red", width: "100%", padding: "20px" }}
        label="Cancel"
        onClick={() => {
          clearInterval(tick.current);
          input.value = "";
          toast.clear();
        }}
      ></Button>
    </div>
  );
};

const Station = ({ chefWS, setStation, readyState }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { stationId } = useParams();
  const wsPingPongIntervalId = useRef(null);
  const scannerThrottle = useRef(null);
  const toastRef = useRef(null);
  const { data: stations, isSuccess: isGetStationsSuccess } =
    useGetStationListQuery(getSubdomain());

  const [statModalOpened, setStatModalOpened] = useState(false);
  const [orders, setOrders] = useState([]);
  const [isVisibleTotal, setIsVisibleTotal] = useState(true);
  const [newOrder, setNewOrder] = useState(false);
  const [chefClicked, setChefClicked] = useState(false);
  const [menuItemsDrawerVisible, setMenuItemsDrawerVisible] = useState(false);
  const [openedAcc, setOpenedAcc] = useState(0);
  const [completeClicked, setCompleteClicked] = useState(false);

  const {
    currentStation,
    setCurrentStation,
    sendEmailModalOpen,
    setSendEmailModalOpen,
  } = useSendStation();

  const messageListenerAdded = useRef(false);
  const totalOrdersElement = useRef(null);
  const statRef = useRef(null);
  const scanBuffer = useRef("");
  const scanTimer = useRef(null);
  const ordersRef = useRef(orders);
  const printStat = useReactToPrint({ contentRef: statRef });

  const {
    data: statistic,
    isSuccess: isGetChefStatisticSuccess,
    isError: isGetChefStatisticError,
  } = useGetChefStatisticQuery({
    id: stationId,
    subDomain: getSubdomain(),
  });
  const {
    data: stationOptions,
    isSuccess: isGetStationOptionsSuccess,
    isError: isGetStationOptionsError,
  } = useGetStationOptionsListQuery({
    id: stationId,
    subDomain: getSubdomain(),
  });
  const [
    makeOrderCompleted,
    {
      isSuccess: isMakeOrderCompletedSuccess,
      isError: isMakeOrderCompletedError,
      error: makeOrderCompletedError,
      isLoading: isMakeOrderCompletedLoading,
    },
  ] = useMakeOrderCompletedMutation();

  const [
    updateOption,
    {
      isSuccess: isUpdateOptionSuccess,
      isError: isUpdateOptionError,
      error: updateOptionError,
    },
  ] = useUpdateOptionMutation();

  const [updateOptionGroup] = useUpdateOptionGroupMutation();
  const autoGroupDisableRef = useRef(false);

  const showToast = useContext(ToastContext);

  useEffect(() => {
    dispatch(setLoading(true));

    window.addEventListener("keydown", (e) => {
      if (scanTimer.current) clearTimeout(scanTimer.current);

      // Usually scanner sends only printable characters and ends with Enter
      if (e.key === "Enter") {
        if (scanBuffer.current.length >= 4) {
          console.log("Scanned barcode:", scanBuffer.current);
          console.log(
            ordersRef.current,
            scanBuffer.current,
            convertToNormal(scanBuffer.current),
          );
          const order = ordersRef.current.find(
            (order) => +order.id === +convertToNormal(scanBuffer.current),
          );
          if (order) {
            toastRef.current?.show({
              sticky: true,
              severity: "error",
              content: (props) => {
                return (
                  <WarningComplete
                    id={order.id}
                    orderNumber={order.number}
                    toast={toastRef.current}
                    makeOrderCompleted={makeOrderCompleted}
                    input={e.target}
                  />
                );
              },
            });
          } else {
            showToast("error", "Error", "Order not found");
            e.target.value = "";
          }
        }
        scanBuffer.current = "";
        return;
      }

      if (e.key && e.key.length === 1) {
        scanBuffer.current += e.key;
      }

      // Reset if user types too slowly (not scanner)
      scanTimer.current = setTimeout(() => {
        scanBuffer.current = "";
      }, 50);
    });

    return () => {
      window.removeEventListener("keydown", (e) => {
        if (scanTimer.current) clearTimeout(scanTimer.current);
        scanBuffer.current = "";
      });
    };
  }, []);

  useEffect(() => {
    if (isUpdateOptionSuccess || isUpdateOptionError) {
      dispatch(setLoading(false));
    }
  }, [isUpdateOptionSuccess, isUpdateOptionError]);

  useEffect(() => {
    if (!chefWS) return;

    console.log(chefWS);

    if (readyState) {
      chefWS.send(
        JSON.stringify({
          type: "get-orders",
          data: {
            station_id: +stationId,
          },
        }),
      );

      document.addEventListener(
        "scroll",
        () => {
          if (!totalOrdersElement.current) {
            setIsVisibleTotal(false);
            return;
          }
          const top = totalOrdersElement.current.getBoundingClientRect().top;
          setIsVisibleTotal(top <= 0 && top >= window.innerHeight);
        },
        true,
      );
    }
  }, [chefWS, readyState]);

  useEffect(() => {
    if (stationId && chefWS?.readyState === 1) {
      setStation(+stationId);
      chefWS.send(
        JSON.stringify({
          type: "get-orders",
          data: {
            station_id: +stationId,
          },
        }),
      );
    }
  }, [stationId, chefWS]);

  useEffect(() => {
    if (isMakeOrderCompletedSuccess) {
      chefWS.send(
        JSON.stringify({
          type: "get-orders",
          data: {
            station_id: +stationId,
          },
        }),
      );
      dispatch(statisticApi.util.invalidateTags(["Statistic"]));
    }
  }, [isMakeOrderCompletedSuccess]);

  useEffect(() => {
    if (isMakeOrderCompletedError) {
      showToast(
        "error",
        makeOrderCompletedError?.status,
        makeOrderCompletedError?.data?.message ||
          (makeOrderCompletedError?.data?.errors && "Incorrect data"),
      );
    }
  }, [isMakeOrderCompletedError]);

  useEffect(() => {
    if (isGetStationOptionsError) {
      showToast("error", "Station Option", "Unable to load station options");
    }
  }, [isGetStationOptionsError]);

  useEffect(() => {
    if (isGetChefStatisticError) {
      showToast("error", "Statistic", "Unable to load statistics");
    }
  }, [isGetChefStatisticError]);

  useEffect(() => {
    if (isGetStationsSuccess && chefWS && !messageListenerAdded.current) {
      const station = stations.find((station) => +station.id === +stationId);
      if (station) {
        setCurrentStation(station);

        const handleMessage = (event) => {
          const data = JSON.parse(event.data);
          if (data.type === "orders-list") {
            setOrders(data.data.sort((a, b) => +a.id - +b.id));
            ordersRef.current = data.data.sort((a, b) => +a.id - +b.id);
          }
          if (data?.code === 401) {
            navigate("/login");
          }
          if (data.type === "update-orders") {
            chefWS.send(
              JSON.stringify({
                type: "get-orders",
                data: {
                  station_id: +stationId,
                },
              }),
            );
          }
          if (data.type === "update-options") {
            dispatch(
              foodStationOptionsApi.util.invalidateTags([
                { type: "FoodStationOptions" },
              ]),
            );
          }
          if (data.type === "update-stations") {
            dispatch(
              foodStationApi.util.invalidateTags([{ type: "FoodStations" }]),
            );
          }
          if (data.type === "new-order") {
            console.log(data.data.order);
            setOrders((prev) => [...prev, data.data.order]);
            ordersRef.current = [...ordersRef.current, data.data.order];
            setNewOrder(true);

            if (window.electronAPI?.print) {
              const order = data.data.order;

              const dataToPrint = [
                { type: "order-id", value: convertForReader(order.id) },
                { type: "divider" },
                { type: "text", value: station.name },
                { type: "divider" },
                { type: "text", value: `ORDER № ${data.data.order.number}` },
                { type: "divider" },
                { type: "text-big", value: `${order.name} ${order.surname}` },
                { type: "divider" },
                { type: "text", value: order.dining_option_name },
                { type: "title", value: "ALLERGIES" },
                {
                  type: "text-bold-upper",
                  value: `${(order.allergies || []).reduce(
                    (acc, current, index, array) => {
                      if (index === array.length - 1) {
                        return acc + current;
                      } else {
                        return acc + current + ", ";
                      }
                    },
                    "",
                  )}`,
                },
              ];

              const menuToPrint = sortMenuByStationOptions(
                order.menu,
                stationOptions,
              );

              menuToPrint.length &&
                menuToPrint.forEach((item) => {
                  dataToPrint.push({
                    type: "title",
                    value: item.group_name,
                  });
                  dataToPrint.push({
                    type: "text",
                    value: (() => {
                      return item.options
                        ? item.options.reduce(
                            (res, option) => res + option.option_name + ", ",
                            "",
                          )
                        : "";
                    })(),
                  });
                });

              dataToPrint.push({ type: "title", value: "Special Notes" });
              dataToPrint.push({
                type: "text-small",
                value: order.special_note,
              });

              dataToPrint.push({
                type: "title",
                value: "Order Date Time",
              });
              dataToPrint.push({
                type: "text",
                value: format(
                  parse(order.creation_time, "yyyy-MM-dd HH:mm", new Date()),
                  "yyyy-MM-dd hh:mm aa",
                ),
              });
              dataToPrint.push({ type: "divider" });
              dataToPrint.push({
                type: "text-big",
                value: `${order.name} ${order.surname}`,
              });
              dataToPrint.push({ type: "divider" });

              window.electronAPI.print(dataToPrint);
            }

            setTimeout(() => {
              setNewOrder(false);
            }, 2000);

            const audio = new Audio(
              `${process.env.BACKEND_URL.replace("/api", "")}/public/sounds/${
                station.sound_name
              }`,
            );
            audio.play();
          }
        };

        chefWS.addEventListener("message", handleMessage);
        messageListenerAdded.current = true;

        return () => {
          chefWS.removeEventListener("message", handleMessage);
          messageListenerAdded.current = false;
        };
      }
    }
  }, [isGetStationsSuccess, chefWS, stations, stationId]);

  const completeOrder = (data) => {
    if (!isMakeOrderCompletedLoading) {
      makeOrderCompleted(data);
    }
  };

  const notificationTemplate = (value) => {
    if (value.title === "push") return "✅";
    return "";
  };

  const downloadStatisticPdf = async () => {
    try {
      const pdfFile = await generateReportFile({
        currentStation,
        reportFormat: "pdf",
        statRef,
      });

      const downloadUrl = URL.createObjectURL(pdfFile);
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = pdfFile.name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error(error);
      showToast("error", "Statistic", "Unable to generate PDF report");
    }
  };

  return (
    <div style={{ position: "relative" }}>
      {!isVisibleTotal && (
        <div className={`${styles.indicator}`}>
          <div className={newOrder ? styles.playAnimation : ""}>
            {orders && orders.length}
          </div>
          <div>ORDERS</div>
        </div>
      )}

      {currentStation && (
        <div className={styles.controlsWrapper}>
          <div className={styles.countsButton}>
            <Button
              label="Station Counts"
              icon="pi pi-chart-bar"
              onClick={() => {
                setStatModalOpened(true);
              }}
            />

            <Sidebar
              visible={menuItemsDrawerVisible}
              onHide={() => setMenuItemsDrawerVisible(false)}
              className={styles.sidebar}
              header={() => {
                return <h2>Visibility Settings</h2>;
              }}
            >
              <div className={styles.sidebarContent}>
                <Accordion
                  activeIndex={openedAcc}
                  multiple
                  onTabClose={({ originalEvent, index }) => {
                    const attr =
                      originalEvent.target.getAttribute("data-pc-section");
                    if (attr !== "root" && attr !== "icon") {
                      setOpenedAcc(index);
                    }
                  }}
                  onTabOpen={({ originalEvent, index }) => {
                    const attr =
                      originalEvent.target.getAttribute("data-pc-section");
                    if (attr !== "root" && attr !== "icon") {
                      setOpenedAcc(index);
                    }
                  }}
                  onTabChange={({ originalEvent, index }) => {
                    const attr =
                      originalEvent.target.getAttribute("data-pc-section");
                    if (attr !== "root" && attr !== "icon") {
                      setOpenedAcc(index);
                    }
                  }}
                >
                  {isGetStationOptionsSuccess &&
                    stationOptions.map((item) => {
                      return (
                        <AccordionTab
                          header={
                            <div>
                              <div>
                                <span>{item.group.group_name}</span>
                                {"  "}
                                <span>
                                  {item.group.allow_multiple === 1
                                    ? `( Multiple Choice MAX ${item.group.max_multiple} )`
                                    : "( Single Choice)"}
                                </span>
                              </div>
                            </div>
                          }
                        >
                          <div>
                            {item.items.map((menuItem) => {
                              return (
                                <div
                                  key={menuItem.id}
                                  className={styles.itemRow}
                                >
                                  <div>
                                    {menuItem.visible ? (
                                      <Button
                                        icon="pi pi-eye"
                                        className="p-button-text"
                                        onMouseUp={() => {
                                          dispatch(setLoading(true));
                                          updateOption({
                                            id: menuItem.id,
                                            data: {
                                              group_id: item.group.group_id,
                                              visible:
                                                menuItem.visible === "Active",
                                            },
                                            subDomain: getSubdomain(),
                                          });
                                          const allOtherInvisible =
                                            shouldAutoDisableGroup(
                                              item.items,
                                              menuItem.id,
                                            );
                                          if (allOtherInvisible) {
                                            autoGroupDisableRef.current = true;
                                            updateOptionGroup({
                                              id: item.group.group_id,
                                              data: { active: false },
                                              subDomain: getSubdomain(),
                                            });
                                          }
                                        }}
                                      />
                                    ) : (
                                      <Button
                                        icon="pi pi-eye-slash"
                                        className="p-button-text"
                                        onMouseUp={() => {
                                          dispatch(setLoading(true));
                                          updateOption({
                                            id: menuItem.id,
                                            data: {
                                              group_id: item.group.group_id,
                                              visible:
                                                menuItem.visible !== "Active",
                                            },
                                            subDomain: getSubdomain(),
                                          });
                                        }}
                                      />
                                    )}
                                  </div>
                                  <div>{menuItem.name}</div>
                                  <div>{menuItem.note}</div>
                                </div>
                              );
                            })}
                            {/* <OrderList
                          dataKey="id"
                          value={item.items}
                          onChange={(e) => {
                            saveOptionsOrder({
                              payload: {
                                options: e.value.map((item, index) => {
                                  return { id: item.id, order: index + 1 };
                                }),
                                group_id: item.group.group_id,
                              },
                              subDomain: getSubdomain(),
                            });
                          }}
                          header={
                            <div className={styles.header}>
                              <div>Order</div>
                              <div>Title</div>
                              <div>Cost</div>
                              <div>Active</div>
                              <div>Note</div>
                              <div>Actions</div>
                            </div>
                          }
                          itemTemplate={itemTemplate(item)}
                          dragdrop
                        ></OrderList> */}
                          </div>
                        </AccordionTab>
                      );
                    })}
                </Accordion>
              </div>
            </Sidebar>

            <Button
              label="Menu Items Visibility"
              icon={<img src={eyeGearIcon} width={30} />}
              onClick={() => setMenuItemsDrawerVisible(true)}
            />
            {/* {isGetStationOptionsSuccess && (
            <>
              <ButtonGroup>
                {stationOptions.map((option, index) => {
                  return (
                    <Button
                      label={option.group.group_name}
                      icon="pi pi-cog"
                      onClick={() => {
                        openMenuOptionsModal(true, index);
                      }}
                    />
                  );
                })}
              </ButtonGroup>

              <Dialog
                visible={optionModalOpened}
                header={`Edit ${stationOptions[selectedGroup]?.group.group_name}`}
                onHide={() => {
                  setOptionModalOpened(false);
                }}
              >
                {stationOptions[selectedGroup] && (
                  <div className={styles.listWrapper}>
                    <OrderList
                      dataKey="id"
                      value={stationOptions[selectedGroup].items}
                      onChange={(e) => {
                        saveOptionsOrder({
                          payload: {
                            options: e.value.map((item, index) => {
                              return { id: item.id, order: index + 1 };
                            }),
                            group_id:
                              stationOptions[selectedGroup].group.group_id,
                          },
                          subDomain: getSubdomain(),
                        });
                      }}
                      header={
                        <div className={styles.header}>
                          <div>Title</div>
                          <div>Active</div>
                          <div>Note</div>
                        </div>
                      }
                      itemTemplate={itemTemplate(stationOptions[selectedGroup])}
                    ></OrderList>
                  </div>
                )}
              </Dialog>
            </>
          )} */}
          </div>

          {sendEmailModalOpen && (
            <SendStationStatisticModal statRef={statRef} />
          )}

          {isGetChefStatisticSuccess && isGetStationOptionsSuccess && (
            <Dialog
              draggable={false}
              visible={statModalOpened}
              header={() => (
                <div className={styles.statHeader}>
                  <div>{currentStation.name} Statistic</div>
                  <Button
                    label="Send To Email"
                    className={styles.sendToEmailBtn}
                    onClick={() => setSendEmailModalOpen(true)}
                  />
                  {/* <Button
                    label="Print"
                    icon="pi pi-print"
                    onClick={() => printStat()}
                  /> */}
                </div>
              )}
              className={styles.statModal}
              onHide={() => {
                setStatModalOpened(false);
              }}
            >
              <div ref={statRef} className={styles.statList}>
                <h2 className={styles.statStationName}>
                  {currentStation.name}
                </h2>
                <div className={styles.statFieldsWrapper}>
                  <div className={styles.statFieldItem}>
                    <div className={styles.statFieldItemHeader}>
                      <div>Total Orders</div>
                      <div>Today</div>
                    </div>
                    <div>{statistic?.active_orders ?? 0}</div>
                  </div>
                  <div className={styles.statFieldItem}>
                    <div className={styles.statFieldItemHeader}>
                      <div>Avg Order Time</div>
                      <div>Today</div>
                    </div>
                    <div>{statistic?.average}</div>
                  </div>
                </div>
                <DataTable
                  className={styles.statTable}
                  value={statistic?.completed_orders}
                >
                  <Column field="id" header="Order №" />

                  <Column field="customer_name" header="Customer Name" />

                  {stationOptions.map((option) => {
                    return (
                      <Column
                        key={option.group.group_id}
                        body={(rowData) => {
                          const optionGroup = (rowData.menu || []).find(
                            (item) =>
                              item.group_name === option.group.group_name,
                          );
                          if (optionGroup) {
                            return optionGroup.options
                              .map((option) => option.option_name)
                              .join(", ");
                          }
                          return null;
                        }}
                        header={option.group.group_name}
                      />
                    );
                  })}

                  <Column field="ordered" header="Ordered" />

                  <Column field="completed" header="Completed" />

                  <Column
                    header="Notification"
                    body={(rowData) => {
                      if (rowData.notification) return "Push";
                      if (rowData.email) return rowData.email;
                      if (rowData.phone) return rowData.phone;
                      return "-";
                    }}
                    bodyClassName="text-center"
                  />
                </DataTable>
              </div>
            </Dialog>
          )}
        </div>
      )}

      {currentStation && (
        <div className={styles.ordersWrapper}>
          <Toast ref={toastRef} position="center" />

          <h2 ref={totalOrdersElement}>
            {orders && orders.length
              ? `${orders.length} Total Orders`
              : "No orders to display"}
          </h2>
          {chefClicked ? (
            <div className={styles.orders}>
              {orders.map((order) => (
                <OrderCard
                  key={order.id}
                  order={order}
                  makeOrderCompleted={completeOrder}
                  selectedStation={currentStation}
                  stationOptions={stationOptions}
                />
              ))}
            </div>
          ) : (
            <div className={styles.startButtonWrapper}>
              <Button
                label="Start"
                className={styles.startButton}
                onClick={() => setChefClicked(true)}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Station;
